import { create } from 'zustand'
import { persist } from 'zustand/middleware'

interface SurveyProgress {
  currentPage: number
  answers: Record<string, number>
  lastUpdated: number
}

interface SurveyState {
  // Store progress for multiple surveys by assessmentResponseId
  surveys: Record<string, SurveyProgress>

  // Actions
  setSurveyProgress: (
    assessmentResponseId: string,
    currentPage: number,
    answers: Record<string, number>,
  ) => void
  getSurveyProgress: (assessmentResponseId: string) => SurveyProgress | null
  clearSurveyProgress: (assessmentResponseId: string) => void
  clearAllSurveys: () => void
}

export const useSurveyStore = create<SurveyState>()(
  persist(
    (set, get) => ({
      surveys: {},

      setSurveyProgress: (assessmentResponseId, currentPage, answers) => {
        set((state) => ({
          surveys: {
            ...state.surveys,
            [assessmentResponseId]: {
              currentPage,
              answers,
              lastUpdated: Date.now(),
            },
          },
        }))
      },

      getSurveyProgress: (assessmentResponseId) => {
        const progress = get().surveys[assessmentResponseId]
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        return progress || null
      },

      clearSurveyProgress: (assessmentResponseId) => {
        set((state) => {
          const { [assessmentResponseId]: _, ...rest } = state.surveys
          return { surveys: rest }
        })
      },

      clearAllSurveys: () => {
        set({ surveys: {} })
      },
    }),
    {
      name: 'survey-storage',
      // Only persist surveys updated in the last 30 days
      partialize: (state) => ({
        surveys: Object.fromEntries(
          Object.entries(state.surveys).filter(
            ([_, progress]) =>
              Date.now() - progress.lastUpdated < 30 * 24 * 60 * 60 * 1000,
          ),
        ),
      }),
    },
  ),
)
